import React, { useState } from "react";
import { Input, Spinner } from "../ui";
import { t } from "../../i18n";

interface SuggestInputProps<T> {
  value: string;
  onChange: (value: string) => void;
  /** Depois de sair do campo (ex.: capitalizar o nome). */
  onBlur?: () => void;
  onEnter?: () => void;
  placeholder: string;
  disabled?: boolean;
  searching: boolean;
  suggestions: T[];
  getKey: (item: T) => React.Key;
  renderSuggestion: (item: T) => React.ReactNode;
  onSelect: (item: T) => void;
}

// Campo com lista de sugestões embaixo. A busca (debounce, filtro) fica com quem usa;
// aqui só abre/fecha a lista e mostra o "Buscando...".
export function SuggestInput<T>({
  value, onChange, onBlur, onEnter, placeholder, disabled, searching, suggestions, getKey, renderSuggestion, onSelect,
}: SuggestInputProps<T>) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <Input
        type="text"
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Espera o clique na sugestão chegar antes de fechar a lista.
          setTimeout(() => setOpen(false), 150);
          onBlur?.();
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          onEnter?.();
        }}
      />

      {open && (searching || suggestions.length > 0) && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-control border border-line bg-surface shadow-popover">
          {searching ? (
            <li className="flex items-center gap-2 px-3 py-2.5 text-caption text-fg-subtle">
              <Spinner size={12} /> {t("links.searching")}
            </li>
          ) : (
            suggestions.map((item) => (
              <li key={getKey(item)}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setOpen(false);
                    onSelect(item);
                  }}
                  className="w-full px-3 py-2.5 text-left text-body text-fg transition-colors duration-fast hover:bg-primary-tint"
                >
                  {renderSuggestion(item)}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
